/**
 * Malay Narrative Writing activity registry (Phase 2C/2E).
 * Display titles are UI copy only — stable keys are used for routing.
 * Default sessionReady is false for Learning Tutor until server override;
 * Writing Studio defaults ready (Phase 2E).
 */

export type MalayNarrativeWritingActivityKey =
  | 'malay_narrative_learning_tutor'
  | 'malay_narrative_writing_studio';

export type MalayNarrativeWritingActivityType =
  | 'narrative_learning'
  | 'narrative_writing_studio';

export interface MalayNarrativeWritingActivity {
  key: MalayNarrativeWritingActivityKey;
  activityType: MalayNarrativeWritingActivityType;
  featureKey: 'narrative_writing';
  title: string;
  categoryLabel: string;
  description: string;
  ctaLabel: string;
  /** False until server reports session_ready (Learning) or Studio override false. */
  sessionReady: boolean;
  disabledReason: string;
}

export const MALAY_NARRATIVE_WRITING_ACTIVITIES: readonly MalayNarrativeWritingActivity[] = [
  {
    key: 'malay_narrative_learning_tutor',
    activityType: 'narrative_learning',
    featureKey: 'narrative_writing',
    categoryLabel: 'Narrative Learning',
    title: 'Narrative Learning Tutor',
    description:
      'Learn mystery narrative writing with Cikgu AIn — plot, conflict, suspense, and resolution.',
    ctaLabel: 'Start with Cikgu AIn',
    sessionReady: false,
    disabledReason: 'Narrative Learning Tutor is not ready for your school yet.',
  },
  {
    key: 'malay_narrative_writing_studio',
    activityType: 'narrative_writing_studio',
    featureKey: 'narrative_writing',
    categoryLabel: 'Writing Studio',
    title: 'Writing Studio & Review',
    description:
      'Type or paste your story, keep a plot plan beside you, and save drafts securely.',
    ctaLabel: 'Open Writing Studio',
    sessionReady: true,
    disabledReason: 'Writing Studio is not available for your school yet.',
  },
] as const;

const BY_KEY: Record<MalayNarrativeWritingActivityKey, MalayNarrativeWritingActivity> =
  MALAY_NARRATIVE_WRITING_ACTIVITIES.reduce(
    (acc, activity) => {
      acc[activity.key] = activity;
      return acc;
    },
    {} as Record<MalayNarrativeWritingActivityKey, MalayNarrativeWritingActivity>
  );

export function isMalayNarrativeWritingActivityKey(
  value: string | null | undefined
): value is MalayNarrativeWritingActivityKey {
  if (!value) return false;
  return Object.prototype.hasOwnProperty.call(BY_KEY, value);
}

export function getMalayNarrativeWritingActivity(
  key: string | null | undefined
): MalayNarrativeWritingActivity | null {
  if (!isMalayNarrativeWritingActivityKey(key)) return null;
  return BY_KEY[key];
}

export function listMalayNarrativeWritingActivities(): readonly MalayNarrativeWritingActivity[] {
  return MALAY_NARRATIVE_WRITING_ACTIVITIES;
}

/**
 * Apply server session_ready overrides.
 * Studio defaults true when override is omitted; pass false to disable.
 */
export function withNarrativeSessionReadyOverrides(
  overrides?: Partial<Record<MalayNarrativeWritingActivityKey, boolean>> | null
): MalayNarrativeWritingActivity[] {
  return MALAY_NARRATIVE_WRITING_ACTIVITIES.map((activity) => {
    const hasOverride = Boolean(overrides && activity.key in overrides);
    const ready = hasOverride
      ? Boolean(overrides![activity.key])
      : activity.sessionReady;

    if (activity.key === 'malay_narrative_writing_studio') {
      return {
        ...activity,
        sessionReady: ready,
        disabledReason: ready
          ? ''
          : 'Writing Studio is not available for your school yet.',
      };
    }

    return {
      ...activity,
      sessionReady: ready,
      disabledReason: ready
        ? ''
        : 'Narrative Learning Tutor is not ready for your school yet.',
    };
  });
}
